import { useCallback } from 'react';
import type Handsontable from 'handsontable';
import {
  RESIZE_AXIS_CONFIG,
  RESIZE_EDGE_THRESHOLD,
  TResizeAxis,
} from '../resizeAxisConfig';

/**
 * 行/列拉伸 handle 安装器（hook 版）
 *
 * 因为隐藏了行头/列头，HOT 自带 ManualRowResize/ManualColumnResize 的原生交互
 * （handle + guide）无法触发（强绑在行头/列头 TH 上）。
 * 保留官方「数据变更」公开 API `plugin.setManualSize(index, size)` ——
 * 触发 beforeRowResize/afterRowResize hook、兼容 undo；
 * hover 检测 + 可视 handle + 拖拽 guide 完全自建，
 * 依 RESIZE_AXIS_CONFIG 参数化跑行/列两条对称路径。
 *
 * 触发区域：
 * - 列 grip：仅首行（row=0 语义表头）右边 RESIZE_EDGE_THRESHOLD 内 → 列宽拉伸
 * - 行 grip：仅首列（col=0 带拖拽 icon）底边 RESIZE_EDGE_THRESHOLD 内 → 行高拉伸
 *
 * @returns install(hot)：在 afterInit 中调用，返回 teardown，afterDestroy 时调用即可
 */
export const useResizeHandles = (): ((hot: Handsontable) => () => void) => {
  return useCallback((hot: Handsontable): (() => void) => {
    const rootEl = hot.rootElement as HTMLElement;

    // grip：hover 时显示的短粗握把方块；guide：mousedown 后显示的贯穿蓝线
    const grips: Record<TResizeAxis, HTMLDivElement> = {
      row: document.createElement('div'),
      col: document.createElement('div'),
    };
    const guides: Record<TResizeAxis, HTMLDivElement> = {
      row: document.createElement('div'),
      col: document.createElement('div'),
    };
    grips.row.className = 'dba-smart-table-row-resize-grip';
    grips.col.className = 'dba-smart-table-col-resize-grip';
    guides.row.className = 'dba-smart-table-row-resize-guide';
    guides.col.className = 'dba-smart-table-col-resize-guide';
    rootEl.appendChild(grips.row);
    rootEl.appendChild(grips.col);
    rootEl.appendChild(guides.row);
    rootEl.appendChild(guides.col);

    const activeIndex: Record<TResizeAxis, number | null> = { row: null, col: null };
    let dragging: TResizeAxis | null = null;

    const hideGrip = (axis: TResizeAxis) => {
      if (activeIndex[axis] === null) return;
      Object.assign(grips[axis].style, { display: 'none' });
      activeIndex[axis] = null;
    };
    const hideAllGrips = () => {
      hideGrip('row');
      hideGrip('col');
    };
    const hideGuide = (axis: TResizeAxis) => {
      Object.assign(guides[axis].style, { display: 'none' });
    };

    const showGrip = (axis: TResizeAxis, index: number, rect: DOMRect, rootRect: DOMRect) => {
      activeIndex[axis] = index;
      Object.assign(grips[axis].style, RESIZE_AXIS_CONFIG[axis].gripStyle(rect, rootRect, rootEl));
    };

    const startResize = (axis: TResizeAxis, e: MouseEvent) => {
      const idx = activeIndex[axis];
      if (idx === null) return;
      e.preventDefault();
      e.stopPropagation();
      const cfg = RESIZE_AXIS_CONFIG[axis];
      const startRect = cfg.getStartRect(hot, idx);
      if (!startRect) return;
      const startSize = cfg.getStartSize(startRect);
      const start = { x: e.clientX, y: e.clientY };
      dragging = axis;
      document.body.classList.add('dba-smart-table-resizing');

      // 进入拖拽：隐藏 grip，显示贯穿 guide 在起始位置
      hideAllGrips();
      const initRootRect = rootEl.getBoundingClientRect();
      Object.assign(guides[axis].style, cfg.guideStartStyle(startRect, initRootRect, rootEl));

      const onMove = (ev: MouseEvent) => {
        const newSize = Math.max(cfg.min, startSize + cfg.getDelta(ev, start));
        const rootRect = rootEl.getBoundingClientRect();
        Object.assign(guides[axis].style, cfg.guideMovingStyle(startRect, newSize, rootRect));
      };
      const onUp = (ev: MouseEvent) => {
        document.removeEventListener('mousemove', onMove, true);
        document.removeEventListener('mouseup', onUp, true);
        try {
          const newSize = Math.max(cfg.min, startSize + cfg.getDelta(ev, start));
          if (Math.abs(newSize - startSize) >= 1) {
            const plugin = hot.getPlugin(cfg.pluginName) as unknown as {
              setManualSize?: (index: number, size: number) => number;
            } | undefined;
            if (plugin?.setManualSize) {
              plugin.setManualSize(idx, newSize);
              hot.render();
            }
          }
        } finally {
          // 兜底：无论是否异常都恢复全局状态
          document.body.classList.remove('dba-smart-table-resizing');
          dragging = null;
          hideGuide(axis);
        }
      };
      document.addEventListener('mousemove', onMove, true);
      document.addEventListener('mouseup', onUp, true);
    };

    const onRootMouseMove = (e: MouseEvent) => {
      if (dragging) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      // 鼠标停留在 grip 上时保持显示，避免闪烁
      if (target.closest('.dba-smart-table-row-resize-grip, .dba-smart-table-col-resize-grip')) return;

      const td = target.closest('td') as HTMLElement | null;
      if (!td) {
        hideAllGrips();
        return;
      }

      let coords: Handsontable.CellCoords | null = null;
      try {
        coords = hot.getCoords(td) as Handsontable.CellCoords | null;
      } catch {
        coords = null;
      }
      if (!coords || coords.row < 0 || coords.col < 0) {
        hideAllGrips();
        return;
      }

      const tdRect = td.getBoundingClientRect();
      const rootRect = rootEl.getBoundingClientRect();
      const nearBottom = tdRect.bottom - e.clientY >= 0 && tdRect.bottom - e.clientY <= RESIZE_EDGE_THRESHOLD;
      const nearRight = tdRect.right - e.clientX >= 0 && tdRect.right - e.clientX <= RESIZE_EDGE_THRESHOLD;

      // grip 直接基于 tdRect 居中（行高 grip 居于首列底边、列宽 grip 居于首行右边）
      if (nearRight && coords.row === 0) {
        hideGrip('row');
        if (activeIndex.col !== coords.col) showGrip('col', coords.col, tdRect, rootRect);
      } else if (nearBottom && coords.col === 0) {
        hideGrip('col');
        if (activeIndex.row !== coords.row) showGrip('row', coords.row, tdRect, rootRect);
      } else {
        hideAllGrips();
      }
    };

    const onRootMouseLeave = () => {
      if (!dragging) hideAllGrips();
    };

    // 滚动时 td rect 失效，隐藏 grip 避免停留在旧坐标（拖拽中 guide 由 mousemove 持续修正，无需处理）
    const wtHolder = rootEl.querySelector('.wtHolder') as HTMLElement | null;
    const onScroll = () => {
      if (!dragging) hideAllGrips();
    };
    const onRowMouseDown = (e: MouseEvent) => startResize('row', e);
    const onColMouseDown = (e: MouseEvent) => startResize('col', e);

    rootEl.addEventListener('mousemove', onRootMouseMove);
    rootEl.addEventListener('mouseleave', onRootMouseLeave);
    grips.row.addEventListener('mousedown', onRowMouseDown);
    grips.col.addEventListener('mousedown', onColMouseDown);
    wtHolder?.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      rootEl.removeEventListener('mousemove', onRootMouseMove);
      rootEl.removeEventListener('mouseleave', onRootMouseLeave);
      grips.row.removeEventListener('mousedown', onRowMouseDown);
      grips.col.removeEventListener('mousedown', onColMouseDown);
      wtHolder?.removeEventListener('scroll', onScroll);
      grips.row.parentElement?.removeChild(grips.row);
      grips.col.parentElement?.removeChild(grips.col);
      guides.row.parentElement?.removeChild(guides.row);
      guides.col.parentElement?.removeChild(guides.col);
      document.body.classList.remove('dba-smart-table-resizing');
    };
  }, []);
};
